import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're NOT already on the login page
      // and if we actually have a token (meaning it's an auth failure, not a login attempt)
      const currentPath = window.location.pathname;
      const hasToken = localStorage.getItem('token');

      if (currentPath !== '/login' && hasToken) {
        // Token expired or invalid - clear and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  register: async (username, email, password, fullName) => {
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
      fullName,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },
};

// Metrics API
export const metricsAPI = {
  getLatestAll: async (companyId = null) => {
    const params = companyId ? { companyId } : {};
    const response = await api.get('/metrics/data/latest/all', { params });
    return response.data;
  },

  getLatest: async (metricKey, companyId = null) => {
    const params = companyId ? { companyId } : {};
    const response = await api.get(`/metrics/data/${metricKey}/latest`, { params });
    return response.data;
  },

  getHistory: async (metricKey, params = {}) => {
    const response = await api.get(`/metrics/data/${metricKey}/history`, {
      params,
    });
    return response.data;
  },

  getDefinitions: async (companyId = null) => {
    const params = companyId ? { companyId } : {};
    const response = await api.get('/metrics/definitions', { params });
    return response.data;
  },

  submitMetric: async (metricKey, value, timestamp) => {
    const response = await api.post('/metrics/data', {
      metricKey,
      value,
      timestamp,
    });
    return response.data;
  },
};

// Dashboards API
export const dashboardsAPI = {
  getAll: async () => {
    const response = await api.get('/dashboards');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/dashboards/${id}`);
    return response.data;
  },

  create: async (name, companyId, isDefault = false, staleThresholdMinutes = 5) => {
    const response = await api.post('/dashboards', {
      name,
      companyId,
      isDefault,
      staleThresholdMinutes,
      layout: { cols: 12, rowHeight: 100 }
    });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/dashboards/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/dashboards/${id}`);
    return response.data;
  },

  addWidget: async (dashboardId, metricKey, position, config = {}) => {
    const response = await api.post(`/dashboards/${dashboardId}/widgets`, {
      metricKey,
      position,
      config,
    });
    return response.data;
  },

  updateWidget: async (dashboardId, widgetId, data) => {
    const response = await api.put(
      `/dashboards/${dashboardId}/widgets/${widgetId}`,
      data
    );
    return response.data;
  },

  updateWidgetPositions: async (dashboardId, widgets) => {
    const response = await api.put(
      `/dashboards/${dashboardId}/widgets/positions`,
      { widgets }
    );
    return response.data;
  },

  deleteWidget: async (dashboardId, widgetId) => {
    const response = await api.delete(
      `/dashboards/${dashboardId}/widgets/${widgetId}`
    );
    return response.data;
  },
};

// Companies API
export const companiesAPI = {
  getAll: async () => {
    const response = await api.get('/companies');
    return response.data;
  },

  getMyCompanies: async () => {
    const response = await api.get('/companies/my-companies');
    return response.data;
  },

  getById: async (companyId) => {
    const response = await api.get(`/companies/${companyId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/companies', data);
    return response.data;
  },

  update: async (companyId, data) => {
    const response = await api.put(`/companies/${companyId}`, data);
    return response.data;
  },

  delete: async (companyId) => {
    const response = await api.delete(`/companies/${companyId}`);
    return response.data;
  },

  getUsers: async (companyId) => {
    const response = await api.get(`/companies/${companyId}/users`);
    return response.data;
  },

  assignUser: async (companyId, userId) => {
    const response = await api.post(`/companies/${companyId}/users/${userId}`);
    return response.data;
  },

  removeUser: async (companyId, userId) => {
    const response = await api.delete(`/companies/${companyId}/users/${userId}`);
    return response.data;
  },
};

export default api;
